/*
 * 端口标识与字节流的基本类型。
 */

/** 连接状态。`unsupported` 是环境层面的（浏览器没有 Web Serial 或页面非安全上下文）。 */
export type UartStatus = 'unsupported' | 'disconnected' | 'connecting' | 'connected';

export type UartDirection = 'rx' | 'tx';

/** 端口来源，只有 USB 串口才有这两个值；板载 UART 上都是 undefined。 */
export interface UartPortInfo {
  usbVendorId?: number;
  usbProductId?: number;
}

/**
 * 一段收到的或发出的字节。
 *
 * `data` 是**每次 `reader.read()` 的原始返回值**，不做任何按分隔符切分的加工 ——
 * 串口上跑的常常是没有换行符的二进制协议（Modbus RTU、BACnet MS-TP 都是），
 * 按 `0x0a` 切行会让这类报文永远凑不齐一条记录。
 */
export interface UartFrame {
  direction: UartDirection;
  data: Uint8Array;
  /** 毫秒时间戳（`Date.now()`）。 */
  at: number;
}

/**
 * 一条可以拿给用户看的错误。
 *
 * `key` 是 **i18n key（中文原文）**，页面过一遍 `| translate` —— 服务里不该写死展示文案，
 * 仓库的多语言口径是「中文当 key，查不到就原样返回」。
 *
 * `detail` 是浏览器给的原始报错，**原样展示不翻译**：这是给排查问题的人看的，
 * 翻成中文反而对不上搜索引擎和浏览器文档里的说法。
 */
export interface UartError {
  key: string;
  detail?: string;
}

/**
 * USB 厂商 ID → 厂名。只为把 `0x1A86:0x7523` 显示成人看得懂的东西，
 * 认不出就退回纯 ID，**不做任何猜测**（同一个厂商 ID 下有几十种芯片，
 * 按 PID 猜型号十有八九是错的，不如不猜）。
 */
export const USB_VENDOR_NAMES: Readonly<Record<number, string>> = {
  0x0403: 'FTDI',
  0x0483: 'STMicroelectronics',
  0x04d8: 'Microchip',
  0x067b: 'Prolific',
  0x0d28: 'ARM mbed',
  0x10c4: 'Silicon Labs',
  0x1366: 'SEGGER',
  0x1546: 'u-blox',
  0x1915: 'Nordic Semiconductor',
  0x1a86: '沁恒 WCH',
  0x2341: 'Arduino',
  0x239a: 'Adafruit',
  0x2e8a: 'Raspberry Pi',
  0x303a: 'Espressif',
};
